import React, { useEffect, useState } from "react";
import {
  Table, Button, Modal, Form, Input, Checkbox, Tag,
  message, Space, Tooltip, Popconfirm,
} from "antd";
import {
  PlusOutlined, EditOutlined, DeleteOutlined,
} from "@ant-design/icons";
import { roleApi } from "../../services/api";
import { useAuth } from "../../context/AuthContext";
import useBreakpoint from "../../hooks/useBreakpoint";

export default function RoleManagementPage() {
  const { hasAnyPermission } = useAuth();
  const { isMobile } = useBreakpoint();
  const canEdit = hasAnyPermission("roles:edit");

  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(false);

  // Create/Edit modal
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("create"); // "create" | "edit"
  const [editRole, setEditRole] = useState(null);
  const [form] = Form.useForm();
  const [selectedPermIds, setSelectedPermIds] = useState([]);
  const [saveLoading, setSaveLoading] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [rolesRes, permsRes] = await Promise.all([
        roleApi.getAll(),
        roleApi.getPermissions(),
      ]);
      setRoles(rolesRes.data);
      setPermissions(permsRes.data);
    } catch {
      message.error("Failed to fetch roles");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  // Group permissions by group_name — global permissions are assigned directly to users, not roles
  const permissionGroups = permissions
    .filter((p) => !p.is_global)
    .reduce((groups, p) => {
      const group = p.group_name || "Other";
      if (!groups[group]) groups[group] = [];
      groups[group].push(p);
      return groups;
    }, {});

  const openCreateModal = () => {
    setModalMode("create");
    setEditRole(null);
    form.resetFields();
    setSelectedPermIds([]);
    setModalOpen(true);
  };

  const openEditModal = (role) => {
    setModalMode("edit");
    setEditRole(role);
    form.setFieldsValue({ name: role.name, description: role.description });
    setSelectedPermIds(role.permissions.map((p) => p.id));
    setModalOpen(true);
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaveLoading(true);

      if (modalMode === "create") {
        await roleApi.create({
          name: values.name,
          description: values.description,
          permission_ids: selectedPermIds,
        });
        message.success("Role created");
      } else {
        await roleApi.update(editRole.id, {
          name: values.name,
          description: values.description,
          permission_ids: selectedPermIds,
        });
        message.success("Role updated");
      }

      setModalOpen(false);
      fetchData();
    } catch (err) {
      if (err.response) message.error(err.response.data?.error || "Failed to save role");
    } finally {
      setSaveLoading(false);
    }
  };

  const handleDelete = async (role) => {
    try {
      await roleApi.delete(role.id);
      message.success("Role deleted");
      fetchData();
    } catch (err) {
      message.error(err.response?.data?.error || "Failed to delete role");
    }
  };

  const columns = [
    {
      title: "Role",
      dataIndex: "name",
      render: (text, record) => (
        <div>
          <span className="font-medium">{text}</span>
          {record.is_system && (
            <Tag color="blue" className="ml-2">System</Tag>
          )}
          {record.description && (
            <div className="text-slate-500 text-[12px]">{record.description}</div>
          )}
        </div>
      ),
    },
    {
      title: "Permissions",
      dataIndex: "permissions",
      render: (perms) => (
        <div className="flex flex-wrap gap-1">
          {perms?.filter((p) => !p.is_global).map((p) => (
            <Tag key={p.id} color="geekblue" className="!text-[11px]">
              {p.label}
            </Tag>
          ))}
        </div>
      ),
    },
    {
      title: "Actions",
      width: 100,
      render: (_, record) => (
        <Space size="small">
          {canEdit && (
            <Tooltip title="Edit role">
              <Button type="text" icon={<EditOutlined />} onClick={() => openEditModal(record)} />
            </Tooltip>
          )}
          {canEdit && !record.is_system && (
            <Popconfirm
              title={`Delete role "${record.name}"?`}
              onConfirm={() => handleDelete(record)}
              okText="Delete"
              okButtonProps={{ danger: true }}
            >
              <Tooltip title="Delete role">
                <Button type="text" danger icon={<DeleteOutlined />} />
              </Tooltip>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Role Management</h1>
        <p className="page-subtitle">Manage roles and their permissions</p>
      </div>

      {canEdit && (
        <div className="mb-4">
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreateModal}>
            Create Role
          </Button>
        </div>
      )}

      {!isMobile ? (
        <div className="sq-table">
          <Table
            rowKey="id"
            dataSource={roles}
            columns={columns}
            loading={loading}
            size="middle"
            pagination={false}
          />
        </div>
      ) : (
        <div className="card-list">
          {loading && (
            <div className="empty-state py-6 px-3">
              <div className="empty-state-text">Loading...</div>
            </div>
          )}
          {!loading && roles.length === 0 && (
            <div className="empty-state py-6 px-3">
              <div className="empty-state-text">No roles</div>
            </div>
          )}
          {!loading && roles.map((record) => (
            <div key={record.id} className="card-list-item">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="card-list-item-title">{record.name}</span>
                {record.is_system && <Tag color="blue" className="!m-0">System</Tag>}
              </div>
              {record.description && (
                <div className="card-list-item-sub">{record.description}</div>
              )}
              <div className="card-list-item-meta">
                {record.permissions?.filter((p) => !p.is_global).length > 0 ? (
                  record.permissions.filter((p) => !p.is_global).map((p) => (
                    <Tag key={p.id} color="geekblue" className="!text-[11px] !m-0">{p.label}</Tag>
                  ))
                ) : (
                  <span className="text-[12px] text-slate-400">No permissions</span>
                )}
              </div>
              {canEdit && (
                <div className="card-list-item-actions">
                  <Button size="small" icon={<EditOutlined />} onClick={() => openEditModal(record)}>Edit</Button>
                  {!record.is_system && (
                    <Popconfirm
                      title={`Delete role "${record.name}"?`}
                      onConfirm={() => handleDelete(record)}
                      okText="Delete"
                      okButtonProps={{ danger: true }}
                    >
                      <Button size="small" danger icon={<DeleteOutlined />}>Delete</Button>
                    </Popconfirm>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <Modal
        title={modalMode === "create" ? "Create Role" : `Edit Role — ${editRole?.name || ""}`}
        open={modalOpen}
        onOk={handleSave}
        onCancel={() => setModalOpen(false)}
        confirmLoading={saveLoading}
        width={560}
      >
        <Form form={form} layout="vertical">
          <Form.Item name="name" label="Name" rules={[{ required: true }]}>
            <Input placeholder="Role name" />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input.TextArea rows={2} placeholder="Optional description" />
          </Form.Item>
        </Form>

        <div className="mt-2">
          <div className="font-medium mb-2">Permissions</div>
          {Object.entries(permissionGroups).map(([group, perms]) => (
            <div key={group} className="mb-3">
              <div className="text-slate-500 text-[12px] mb-1">{group}</div>
              {perms.map((p) => (
                <Checkbox
                  key={p.id}
                  checked={selectedPermIds.includes(p.id)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedPermIds((prev) => [...prev, p.id]);
                    } else {
                      setSelectedPermIds((prev) => prev.filter((id) => id !== p.id));
                    }
                  }}
                  className="!block !ml-0 mb-1"
                >
                  {p.label} <span className="text-slate-400 text-[11px]">({p.key})</span>
                </Checkbox>
              ))}
            </div>
          ))}
        </div>
      </Modal>
    </div>
  );
}
